/**
 * Sysadmin Argus Live hooks (AL-C4).
 *
 * Wraps `/admin/platform/argus-live/dashboards/*`. Sibling of
 * `use-argus-live.ts` (tenant-facing) — kept separate because the
 * shape is different (no per-grant effectiveRole; classificationCeiling
 * is sysadmin-only; assignment counts are surfaced).
 *
 * Per the founder's PRIMARY RULE on shared components: this hooks file
 * is intentionally NOT a thin wrapper over use-argus-live with a
 * `scope` argument. The audit hits / RBAC keys / wire shapes differ
 * enough that branching inside one file would inflate the helper.
 * Library Phase 3+4+5 precedent (PR-J) used a parallel-module
 * strategy; AL-C4 follows the same shape.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { apiClient } from './api-client';
import type { ExportControl, Sensitivity } from '@argusai/shared';

// ─── Wire shapes ─────────────────────────────────────────────────────────

export interface ClassificationTag {
  sensitivity: Sensitivity;
  exportControl: ExportControl;
}

export type SystemDashboardStatus = 'draft' | 'published' | 'archived';

export interface SystemDashboard {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  icon: string | null;
  status: SystemDashboardStatus;
  enabled: boolean;
  currentVersionId: string | null;
  schemaVersion: number;
  classificationTag: ClassificationTag;
  classificationCeiling: ClassificationTag | null;
  ownerUserId: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  archivedAt: string | null;
  assignedTenantCount: number;
  cloneCount: number;
}

export interface SystemDashboardVersion {
  id: string;
  dashboardId: string;
  revLabel: string;
  revSequence: number;
  codeHtml: string;
  codeJs: string;
  codeCss: string;
  dataBindings: unknown[];
  sandboxConfig: Record<string, unknown>;
  classificationTag: ClassificationTag;
  contentHash: string;
  authoredByUserId: string;
  createdAt: string;
}

export interface SystemDashboardDetail {
  dashboard: SystemDashboard;
  currentVersion: SystemDashboardVersion | null;
}

export interface SystemDashboardAssignment {
  organizationId: string;
  organizationName: string;
  systemDashboardId: string;
  systemDashboardSlug: string;
  hasCloned: boolean;
  tenantDashboardId: string | null;
  clonedAt: string | null;
}

// ─── Query keys ──────────────────────────────────────────────────────────

export const sysadminArgusLiveKeys = {
  all: ['sysadmin-argus-live'] as const,
  list: (filter?: object) =>
    ['sysadmin-argus-live', 'list', filter ?? {}] as const,
  detail: (id: string) => ['sysadmin-argus-live', 'detail', id] as const,
  versions: (id: string) => ['sysadmin-argus-live', 'versions', id] as const,
  assignments: (id: string) =>
    ['sysadmin-argus-live', 'assignments', id] as const,
};

const BASE = '/admin/platform/argus-live/dashboards';

// ─── List ───────────────────────────────────────────────────────────────

export interface UseSysadminSystemDashboardsParams {
  status?: SystemDashboardStatus;
  includeArchived?: boolean;
  search?: string;
}

export function useSysadminSystemDashboards(
  params: UseSysadminSystemDashboardsParams = {},
  options?: Omit<
    UseQueryOptions<{ items: SystemDashboard[] }, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.includeArchived === true) qs.set('includeArchived', 'true');
  if (params.search) qs.set('search', params.search);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<{ items: SystemDashboard[] }, Error>({
    queryKey: sysadminArgusLiveKeys.list(params),
    queryFn: () =>
      apiClient.get<{ items: SystemDashboard[] }>(`${BASE}${suffix}`),
    ...options,
  });
}

// ─── Detail ─────────────────────────────────────────────────────────────

export function useSysadminSystemDashboard(id: string | undefined) {
  return useQuery<{ data: SystemDashboardDetail }, Error>({
    queryKey: sysadminArgusLiveKeys.detail(id ?? ''),
    queryFn: () =>
      apiClient.get<{ data: SystemDashboardDetail }>(`${BASE}/${id}`),
    enabled: Boolean(id),
  });
}

// ─── Versions ──────────────────────────────────────────────────────────

export function useSysadminSystemDashboardVersions(id: string | undefined) {
  return useQuery<{ items: SystemDashboardVersion[] }, Error>({
    queryKey: sysadminArgusLiveKeys.versions(id ?? ''),
    queryFn: () =>
      apiClient.get<{ items: SystemDashboardVersion[] }>(
        `${BASE}/${id}/versions`,
      ),
    enabled: Boolean(id),
  });
}

// ─── Assignments ───────────────────────────────────────────────────────

export function useSysadminSystemDashboardAssignments(id: string | undefined) {
  return useQuery<{ items: SystemDashboardAssignment[] }, Error>({
    queryKey: sysadminArgusLiveKeys.assignments(id ?? ''),
    queryFn: () =>
      apiClient.get<{ items: SystemDashboardAssignment[] }>(
        `${BASE}/${id}/assignments`,
      ),
    enabled: Boolean(id),
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────

export interface CreateSystemDashboardInput {
  slug: string;
  title: string;
  description?: string | null;
  icon?: string | null;
  classificationCeiling?: ClassificationTag | null;
}

export function useCreateSysadminSystemDashboard() {
  const qc = useQueryClient();
  return useMutation<{ data: SystemDashboard }, Error, CreateSystemDashboardInput>({
    mutationFn: (body) =>
      apiClient.post<{ data: SystemDashboard }>(BASE, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sysadminArgusLiveKeys.all });
    },
  });
}

export interface UpdateSystemDashboardInput {
  id: string;
  title?: string;
  description?: string | null;
  icon?: string | null;
  classificationCeiling?: ClassificationTag | null;
}

export function useUpdateSysadminSystemDashboard() {
  const qc = useQueryClient();
  return useMutation<{ data: SystemDashboard }, Error, UpdateSystemDashboardInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch<{ data: SystemDashboard }>(`${BASE}/${id}`, body),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: sysadminArgusLiveKeys.all });
      void qc.invalidateQueries({
        queryKey: sysadminArgusLiveKeys.detail(vars.id),
      });
    },
  });
}

export function useDeleteSysadminSystemDashboard() {
  const qc = useQueryClient();
  return useMutation<{ success: true }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: true }>(`${BASE}/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sysadminArgusLiveKeys.all });
    },
  });
}

export interface PublishSystemDashboardInput {
  id: string;
  existingVersionId?: string;
  revLabel?: string;
  codeHtml?: string;
  codeJs?: string;
  codeCss?: string;
  dataBindings?: unknown[];
  sandboxConfig?: Record<string, unknown>;
  classificationTag?: ClassificationTag;
}

export function usePublishSysadminSystemDashboard() {
  const qc = useQueryClient();
  return useMutation<
    { data: { dashboard: SystemDashboard; version: SystemDashboardVersion } },
    Error,
    PublishSystemDashboardInput
  >({
    mutationFn: ({ id, ...body }) =>
      apiClient.post(`${BASE}/${id}/publish`, body),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: sysadminArgusLiveKeys.all });
      void qc.invalidateQueries({
        queryKey: sysadminArgusLiveKeys.detail(vars.id),
      });
    },
  });
}

export function useUnpublishSysadminSystemDashboard() {
  const qc = useQueryClient();
  return useMutation<{ data: SystemDashboard }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<{ data: SystemDashboard }>(`${BASE}/${id}/unpublish`, {}),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: sysadminArgusLiveKeys.all });
      void qc.invalidateQueries({
        queryKey: sysadminArgusLiveKeys.detail(vars.id),
      });
    },
  });
}

export function useArchiveSysadminSystemDashboard() {
  const qc = useQueryClient();
  return useMutation<{ data: SystemDashboard }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<{ data: SystemDashboard }>(`${BASE}/${id}/archive`, {}),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: sysadminArgusLiveKeys.all });
      void qc.invalidateQueries({
        queryKey: sysadminArgusLiveKeys.detail(vars.id),
      });
    },
  });
}

export function useUnarchiveSysadminSystemDashboard() {
  const qc = useQueryClient();
  return useMutation<{ data: SystemDashboard }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<{ data: SystemDashboard }>(`${BASE}/${id}/unarchive`, {}),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: sysadminArgusLiveKeys.all });
      void qc.invalidateQueries({
        queryKey: sysadminArgusLiveKeys.detail(vars.id),
      });
    },
  });
}

export function useAssignSysadminSystemDashboard() {
  const qc = useQueryClient();
  return useMutation<
    { success: true },
    Error,
    { id: string; organizationId: string }
  >({
    mutationFn: ({ id, organizationId }) =>
      apiClient.post<{ success: true }>(`${BASE}/${id}/assignments`, {
        organizationId,
      }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({
        queryKey: sysadminArgusLiveKeys.assignments(vars.id),
      });
    },
  });
}

export function useUnassignSysadminSystemDashboard() {
  const qc = useQueryClient();
  return useMutation<
    { success: true },
    Error,
    { id: string; organizationId: string }
  >({
    mutationFn: ({ id, organizationId }) =>
      apiClient.delete<{ success: true }>(
        `${BASE}/${id}/assignments?organizationId=${organizationId}`,
      ),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({
        queryKey: sysadminArgusLiveKeys.assignments(vars.id),
      });
    },
  });
}

// ─── Tenant-facing system-catalog hooks (clone-from-system) ─────────────
// These live here for cohesion with the sysadmin hooks but the tenant
// side calls them too (the tenant API is /api/v1/argus-live/system).

const TENANT_BASE = '/argus-live';

export interface TenantSystemDashboard {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  icon: string | null;
  classificationCeiling: ClassificationTag | null;
  publishedAt: string | null;
}

export function useTenantSystemDashboards(params: { search?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<{ items: TenantSystemDashboard[] }, Error>({
    queryKey: ['tenant-argus-live-system', 'list', params],
    queryFn: () =>
      apiClient.get<{ items: TenantSystemDashboard[] }>(
        `${TENANT_BASE}/system${suffix}`,
      ),
  });
}

export interface CloneFromSystemResult {
  tenantDashboardId: string;
  alreadyCloned: boolean;
  slug: string;
}

export function useCloneFromSystem() {
  const qc = useQueryClient();
  return useMutation<
    { data: CloneFromSystemResult },
    Error,
    {
      systemId: string;
      title?: string;
      tenantClassification?: ClassificationTag;
    }
  >({
    mutationFn: ({ systemId, ...body }) =>
      apiClient.post<{ data: CloneFromSystemResult }>(
        `${TENANT_BASE}/clone-from-system/${systemId}`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['argus-live'] });
    },
  });
}
